import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// FullFrame backup viewer — standalone PAGE (no modal) per user request.
// Loads /data/fullframe-backup.json that was extracted from the user's
// uploaded zip and renders every collection one-to-one:
//   • Manifest + health + tenant info
//   • Company profile + integrations + feature notes
//   • Productions (with expenses, additional, payments)
//   • Suppliers + supplier categories
//   • Company expenses
//   • Credit topups + credit operations + work activities
//   • WhatsApp module (FUS): contacts, messages, flows, templates,
//     campaigns, groups, devices
//
// Built May 2026 — replaces FlowUpViewer modal.

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(iso); }
};
const fmtNIS = (n) => Number(n || 0).toLocaleString('he-IL') + ' ₪';

const Pill = ({ children, color = 'stone' }) => {
  const map = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    purple: 'bg-purple-100 text-purple-800 border-purple-200',
    stone: 'bg-stone-100 text-stone-700 border-stone-200',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    pink: 'bg-pink-100 text-pink-800 border-pink-200',
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${map[color] || map.stone}`}>{children}</span>
  );
};

const Section = ({ title, count, color = 'indigo', children, defaultOpen = true, id }) => {
  const [open, setOpen] = useState(defaultOpen);
  const headerColor = {
    emerald: 'bg-emerald-600',
    amber: 'bg-amber-600',
    blue: 'bg-blue-600',
    purple: 'bg-purple-600',
    indigo: 'bg-indigo-600',
    stone: 'bg-stone-700',
    pink: 'bg-pink-600',
  }[color] || 'bg-stone-700';
  return (
    <section id={id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm scroll-mt-24">
      <button type="button" onClick={() => setOpen(!open)} className={`w-full ${headerColor} text-white px-5 py-3 flex items-center justify-between hover:opacity-95`}>
        <span className="font-black text-base">{title}</span>
        <div className="flex items-center gap-2">
          {count !== undefined && (
            <span className="bg-white/25 text-white text-xs font-bold rounded-full px-2.5 py-0.5">{count}</span>
          )}
          <svg className={`w-4 h-4 transition-transform ${open ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
        </div>
      </button>
      {open && <div className="p-5">{children}</div>}
    </section>
  );
};

const KV = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-2 py-1.5 border-b border-stone-100 last:border-0">
    <span className="text-stone-500 text-xs">{label}</span>
    <span className={`font-bold text-xs text-stone-800 text-left break-all ${mono ? 'font-mono' : ''}`} dir={mono ? 'ltr' : 'auto'}>{value || '—'}</span>
  </div>
);

const FullFrameBackup = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/fullframe-backup.json')
      .then(r => { if (!r.ok) throw new Error('שגיאה בטעינת הגיבוי'); return r.json(); })
      .then(setData)
      .catch(err => setError(err.message));
    document.title = 'פול פריים — גיבוי';
  }, []);

  if (error) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><p className="text-red-600 font-bold">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-stone-50"><p className="text-stone-500 font-bold">טוען...</p></div>;

  const manifest = data.manifest || {};
  const tenant = data.tenant || {};
  const health = data.health || {};
  const meta = data.company_meta || {};
  const integrations = data.integrations || {};
  const featureNotes = data.feature_notes || {};
  const db = data.database || {};
  const wa = data.whatsapp || {};
  const company = (db.companies || [])[0] || {};

  const productions = db.productions || [];
  const suppliers = db.suppliers || [];
  const categories = (db.supplier_categories || [])[0] || { slots: [] };
  const companyExpenses = db.company_expenses || [];
  const topups = db.credit_topups || [];
  const creditOps = db.credit_operations || [];
  const activity = db.work_activities || [];
  const users = db.users || [];

  const totalIncome = productions.reduce((s, p) => s + (p.income || 0), 0);
  const totalProductionExpenses = productions.reduce(
    (s, p) => s + (p.expenses || []).reduce((a, e) => a + (e.amount || 0), 0)
      + (p.additional_expenses || []).reduce((a, e) => a + (e.total_with_vat || 0), 0),
    0,
  );
  const totalCompanyExpenses = companyExpenses.reduce((s, e) => s + (e.total_with_vat || 0), 0);

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl" data-testid="fullframe-page">
      {/* Header */}
      <header className="bg-gradient-to-l from-indigo-700 via-purple-700 to-pink-600 text-white sticky top-0 z-20 shadow-md">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {company.logo_url && (
              <img src={company.logo_url} alt={company.name} className="w-12 h-12 rounded-xl bg-white object-contain p-1" />
            )}
            <div>
              <h1 className="text-2xl font-black tracking-tight">פול פריים</h1>
              <p className="text-indigo-100 text-xs">{company.name || 'Full Frame'} · גיבוי {fmtDate(manifest.created_at)}</p>
            </div>
          </div>
          <Link to="/bari" className="bg-white/15 hover:bg-white/25 text-white text-sm font-bold px-4 py-2 rounded-xl flex items-center gap-1.5 backdrop-blur" data-testid="fullframe-back-link">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            חזרה לדשבורד
          </Link>
        </div>
        {/* Quick nav */}
        <nav className="bg-white/10 backdrop-blur-sm overflow-x-auto">
          <div className="max-w-6xl mx-auto px-5 py-2 flex gap-2 text-[12px] font-bold whitespace-nowrap">
            {[
              ['#overview', 'סקירה'],
              ['#company', 'חברה'],
              ['#productions', 'הפקות'],
              ['#suppliers', 'ספקים'],
              ['#expenses', 'הוצאות'],
              ['#credits', 'קרדיטים'],
              ['#whatsapp', 'WhatsApp'],
              ['#system', 'מערכת'],
            ].map(([h, l]) => (
              <a key={h} href={h} className="text-indigo-100 hover:text-white hover:bg-white/10 px-3 py-1 rounded-lg transition-colors">{l}</a>
            ))}
          </div>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto p-5 space-y-5">
        {/* Overview */}
        <Section id="overview" title="סקירה כללית" color="indigo">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="הכנסות הפקות" value={fmtNIS(totalIncome)} color="emerald" />
            <Card label="הוצאות הפקה" value={fmtNIS(totalProductionExpenses)} color="amber" />
            <Card label="הוצאות חברה" value={fmtNIS(totalCompanyExpenses)} color="red" />
            <Card label="יתרת קרדיטים" value={fmtNIS(company.credit_balance || 0)} color="indigo" />
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(manifest.counts || {}).map(([k, v]) => (
              <div key={k} className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                <p className="text-stone-500 text-[10px]">{k}</p>
                <p className="font-black text-stone-800">{v}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Company */}
        <Section id="company" title="פרטי החברה" color="indigo">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <KV label="שם" value={company.name} />
              <KV label="display" value={company.display_name || company.name} />
              <KV label="איש קשר" value={company.contact_name} />
              <KV label="אימייל" value={company.email} mono />
              <KV label="טלפון" value={company.phone} mono />
              <KV label="חבילה" value={company.package} />
              <KV label="ערוצים" value={(company.channels || []).join(', ')} />
              <KV label="ספק חשבוניות" value={meta.invoice_provider || company.invoice_provider} />
              <KV label="ספק אימייל" value={company.email_provider} />
              <KV label="ספק הודעות" value={company.messaging_provider} />
            </div>
            <div className="space-y-1">
              <KV label="קוד גישה" value={company.access_code} mono />
              <KV label="סה״כ הוטען" value={fmtNIS(company.total_topped_up)} />
              <KV label="סה״כ AI cost" value={fmtNIS(company.total_ai_cost_charged)} />
              <KV label="company_id" value={manifest.company_id} mono />
              <KV label="נוצר" value={fmtDate(company.created_at)} />
              <KV label="עודכן" value={fmtDate(company.updated_at)} />
            </div>
          </div>
          {company.gdrive_folder_url && (
            <a href={company.gdrive_folder_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-indigo-600 text-xs font-bold hover:underline" dir="ltr">📂 Google Drive folder ↗</a>
          )}
          <div className="mt-4 pt-3 border-t border-stone-200">
            <p className="text-xs font-bold text-stone-700 mb-2">תוספים פעילים</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(company.addons_enabled || {}).map(([k, v]) => (
                <Pill key={k} color={v ? 'emerald' : 'stone'}>{k}: {v ? 'פעיל' : 'כבוי'}</Pill>
              ))}
            </div>
          </div>
        </Section>

        {/* Users */}
        <Section title="משתמשים" count={users.length} color="stone" defaultOpen={false}>
          <div className="divide-y divide-stone-100">
            {users.map(u => (
              <div key={u.id} className="flex items-center gap-3 py-2">
                {u.avatar_url && <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-stone-800 truncate">{u.full_name}</p>
                  <p className="text-[11px] text-stone-500 truncate" dir="ltr">{u.email}</p>
                </div>
                <Pill color={u.role === 'owner' ? 'purple' : 'stone'}>{u.role}</Pill>
              </div>
            ))}
          </div>
        </Section>

        {/* Productions */}
        <Section id="productions" title="הפקות" count={productions.length} color="purple">
          {productions.length === 0 ? <p className="text-center text-stone-400 py-6">אין הפקות</p> : productions.map(p => {
            const exTotal = (p.expenses || []).reduce((s, e) => s + (e.amount || 0), 0);
            const exPaid = (p.expenses || []).reduce((s, e) => s + (e.amount_paid || 0), 0);
            const additionalTotal = (p.additional_expenses || []).reduce((s, e) => s + (e.total_with_vat || 0), 0);
            const profit = (p.income || 0) - exTotal - additionalTotal;
            return (
              <div key={p.id} className="bg-stone-50 rounded-xl border border-stone-200 p-4 mb-3 last:mb-0">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-lg font-black text-stone-800">🎬 {p.name}</h3>
                  <Pill color={p.is_completed ? 'emerald' : 'amber'}>{p.is_completed ? '✓ הושלם' : '⏳ פעיל'}</Pill>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-3">
                  <div className="space-y-1">
                    <KV label="לקוח" value={p.client_name} />
                    <KV label="איש קשר" value={p.contact_name} />
                    <KV label="טלפון" value={p.client_phone} mono />
                    <KV label="וואטסאפ" value={p.client_whatsapp} mono />
                    <KV label="אימייל" value={p.client_email} mono />
                    <KV label="גוגל דרייב" value={p.gdrive_folder_url ? <a href={p.gdrive_folder_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">פתח ↗</a> : '—'} />
                  </div>
                  <div className="space-y-1">
                    <KV label="הכנסה" value={<span className="text-emerald-700">{fmtNIS(p.income)}</span>} />
                    <KV label="תקציב לקוח" value={p.client_budget ? fmtNIS(p.client_budget) : '—'} />
                    <KV label="הוצאות הפקה" value={fmtNIS(exTotal)} />
                    <KV label="הוצאות נוספות" value={fmtNIS(additionalTotal)} />
                    <KV label="שולם בפועל" value={fmtNIS(exPaid)} />
                    <KV label="רווח גולמי" value={<span className={profit >= 0 ? 'text-emerald-700' : 'text-red-700'}>{fmtNIS(profit)}</span>} />
                  </div>
                </div>

                {(p.expenses || []).length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-black text-stone-700 mb-2">צוות / הוצאות הפקה ({p.expenses.length})</h4>
                    <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                      {p.expenses.map(e => {
                        const diff = (e.amount_paid || 0) - (e.amount || 0);
                        return (
                          <div key={e.id} className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-stone-800">{e.role}: <span className="font-normal text-stone-600">{e.provider || '—'}</span></p>
                              <p className="text-[11px] text-stone-500" dir="ltr">{e.provider_phone || ''}</p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Pill color="amber">סוכם {fmtNIS(e.amount)}</Pill>
                              <Pill color={diff === 0 ? 'emerald' : (diff > 0 ? 'red' : 'amber')}>שולם {fmtNIS(e.amount_paid)}</Pill>
                              {e.vat_total > 0 && <Pill color="stone">מע״מ {fmtNIS(e.vat_total)}</Pill>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(p.additional_expenses || []).length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-black text-stone-700 mb-2">הוצאות נוספות ({p.additional_expenses.length})</h4>
                    <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                      {p.additional_expenses.map((a, i) => (
                        <div key={a.id || i} className="px-3 py-2 flex items-center justify-between text-xs">
                          <div>
                            <p className="font-bold">{a.product || a.description || '—'}</p>
                            {a.business_name && <p className="text-stone-500 text-[11px]">{a.business_name}</p>}
                          </div>
                          <Pill color="amber">{fmtNIS(a.total_with_vat)}</Pill>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(p.payments || []).length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-black text-stone-700 mb-2">תשלומים ({p.payments.length})</h4>
                    <div className="bg-white rounded-lg border border-stone-200 divide-y divide-stone-100">
                      {p.payments.map((pay, i) => (
                        <div key={pay.id || i} className="px-3 py-2 flex items-center justify-between text-xs">
                          <div>
                            <span className="font-bold">{pay.method}</span>
                            {pay.note && <span className="text-stone-500 mr-2">· {pay.note}</span>}
                            {pay.scheduled_at && <span className="text-amber-700 mr-2">· מתוזמן ל-{fmtDate(pay.scheduled_at)}</span>}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Pill color={pay.paid ? 'emerald' : 'amber'}>{pay.paid ? 'שולם' : 'ממתין'}</Pill>
                            <span className="font-bold">{fmtNIS(pay.amount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-[10px] text-stone-400 mt-3" dir="ltr">id: {p.id} · נוצר {fmtDate(p.created_at)}</p>
              </div>
            );
          })}
        </Section>

        {/* Suppliers */}
        <Section id="suppliers" title="ספקים" count={suppliers.length} color="blue">
          {(categories.slots || []).filter(Boolean).length > 0 && (
            <div className="mb-4 pb-3 border-b border-stone-200">
              <p className="text-xs font-bold text-stone-600 mb-2">קטגוריות:</p>
              <div className="flex flex-wrap gap-1.5">
                {(categories.slots || []).filter(Boolean).map((s, i) => <Pill key={i} color="blue">{s}</Pill>)}
              </div>
            </div>
          )}
          <div className="divide-y divide-stone-100">
            {suppliers.map((s, i) => (
              <div key={s.id || s.name_lower || i} className="py-2 flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-stone-800">{s.name || '—'}</p>
                  <p className="text-[11px] text-stone-500" dir="ltr">{s.phone || ''}{s.email ? ` · ${s.email}` : ''}{s.address ? ` · ${s.address}` : ''}</p>
                  {s.notes && <p className="text-[11px] text-amber-700 mt-0.5">📝 {s.notes}</p>}
                </div>
                {Array.isArray(s.categories) && s.categories.map((c, j) => <Pill key={j} color="blue">{c}</Pill>)}
                {!Array.isArray(s.categories) && s.category && <Pill color="blue">{s.category}</Pill>}
              </div>
            ))}
          </div>
        </Section>

        {/* Company expenses */}
        <Section id="expenses" title="הוצאות החברה" count={companyExpenses.length} color="amber">
          <div className="divide-y divide-stone-100">
            {companyExpenses.map(e => (
              <div key={e.id} className="py-2.5 flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-bold text-sm text-stone-800">{e.business_name || e.product || '—'}</p>
                    <Pill color={e.kind === 'recurring' ? 'purple' : 'stone'}>{e.kind === 'recurring' ? 'קבוע' : 'חד-פעמי'}</Pill>
                  </div>
                  {e.product && e.business_name && <p className="text-[11px] text-stone-500">{e.product}</p>}
                  {e.notes && <p className="text-[11px] text-amber-700">📝 {e.notes}</p>}
                  <p className="text-[10px] text-stone-400">{e.date} {e.end_month ? `· עד ${e.end_month}` : ''}</p>
                </div>
                <div className="text-left">
                  <p className="text-xs text-stone-500">לפני מע״מ: {fmtNIS(e.pre_vat)}</p>
                  <p className="text-sm font-black text-amber-700">{fmtNIS(e.total_with_vat)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t-2 border-amber-200 flex justify-between items-center">
            <span className="font-bold text-sm">סה״כ הוצאות חברה</span>
            <span className="font-black text-lg text-amber-700">{fmtNIS(totalCompanyExpenses)}</span>
          </div>
        </Section>

        {/* Credits */}
        <Section id="credits" title="קרדיטים — טעינות ופעולות" count={topups.length + creditOps.length} color="emerald">
          <h4 className="text-sm font-black text-stone-700 mb-2">טעינות ({topups.length})</h4>
          <div className="divide-y divide-stone-100 mb-4">
            {topups.map(t => (
              <div key={t.id} className="py-2 flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-stone-800">{t.note || `טעינה דרך ${t.method}`}</p>
                  <p className="text-[10px] text-stone-400">{fmtDate(t.created_at)} · {t.method}</p>
                </div>
                <Pill color={t.status === 'approved' ? 'emerald' : (t.status === 'rejected' ? 'red' : 'amber')}>{t.status}</Pill>
                <p className="text-sm font-black text-emerald-700">{t.credits} kr · {fmtNIS(t.amount_nis)}</p>
              </div>
            ))}
          </div>
          <h4 className="text-sm font-black text-stone-700 mb-2 pt-3 border-t border-stone-200">פעולות ({creditOps.length})</h4>
          <div className="divide-y divide-stone-100">
            {creditOps.map(o => (
              <div key={o.id} className="py-2 flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-xs text-stone-800">{o.description || '—'}</p>
                  <p className="text-[10px] text-stone-400">{fmtDate(o.created_at)}</p>
                </div>
                {o.is_adjustment && <Pill color="purple">התאמה</Pill>}
                <p className={`text-xs font-black ${(o.charged_credits || 0) < 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {(o.charged_credits || 0) > 0 ? '−' : '+'}{Math.abs(o.charged_credits || 0).toFixed(2)} kr
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* Activity log */}
        <Section title="יומן פעילות" count={activity.length} color="stone" defaultOpen={false}>
          <div className="divide-y divide-stone-100">
            {activity.map(a => (
              <div key={a.id} className="py-2">
                <p className="text-sm text-stone-800">{a.summary}</p>
                <p className="text-[10px] text-stone-400 mt-0.5">{fmtDate(a.at)}{a.by_email ? ` · ${a.by_email}` : ''}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* WhatsApp / FUS */}
        <Section id="whatsapp" title="WhatsApp · FUS" count={(wa.contacts||[]).length + (wa.messages||[]).length} color="emerald">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <Card label="אנשי קשר" value={(wa.contacts||[]).length} color="emerald" small />
            <Card label="הודעות" value={(wa.messages||[]).length} color="blue" small />
            <Card label="קמפיינים" value={(wa.campaigns||[]).length} color="purple" small />
            <Card label="קבוצות" value={(wa.groups||[]).length} color="indigo" small />
            <Card label="תבניות" value={(wa.templates||[]).length} color="amber" small />
            <Card label="זרימות" value={(wa.flows||[]).length} color="pink" small />
            <Card label="מכשירים" value={(wa.devices||[]).length} color="stone" small />
          </div>
          {(wa.contacts || []).length > 0 && (
            <details className="mb-3 bg-stone-50 rounded-xl border border-stone-200">
              <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold text-stone-700">📇 אנשי קשר ({wa.contacts.length})</summary>
              <div className="px-4 pb-3 divide-y divide-stone-100">
                {wa.contacts.map((c, i) => (
                  <div key={c.id || i} className="py-2 flex items-center justify-between gap-2">
                    <div>
                      <p className="font-bold text-sm">{c.name || c.full_name || '—'}</p>
                      <p className="text-[11px] text-stone-500" dir="ltr">{c.phone || c.wa_id || ''}</p>
                    </div>
                    {c.tags && c.tags.length > 0 && c.tags.map((t, j) => <Pill key={j} color="blue">{t}</Pill>)}
                  </div>
                ))}
              </div>
            </details>
          )}
          {(wa.campaigns || []).length > 0 && (
            <details className="mb-3 bg-stone-50 rounded-xl border border-stone-200">
              <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold text-stone-700">📢 קמפיינים ({wa.campaigns.length})</summary>
              <div className="px-4 pb-3 divide-y divide-stone-100">
                {wa.campaigns.map((c, i) => (
                  <div key={c.id || i} className="py-2">
                    <p className="font-bold text-sm">{c.name || c.title || '—'}</p>
                    <p className="text-[11px] text-stone-500">{c.status || ''} · {fmtDate(c.created_at)}</p>
                    {c.message && <p className="text-[11px] text-stone-600 mt-1 whitespace-pre-wrap">{c.message}</p>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {(wa.templates || []).length > 0 && (
            <details className="mb-3 bg-stone-50 rounded-xl border border-stone-200">
              <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold text-stone-700">📝 תבניות ({wa.templates.length})</summary>
              <div className="px-4 pb-3 divide-y divide-stone-100">
                {wa.templates.map((t, i) => (
                  <div key={t.id || i} className="py-2">
                    <p className="font-bold text-sm">{t.name || '—'}</p>
                    {t.body && <p className="text-[11px] text-stone-600 whitespace-pre-wrap">{t.body}</p>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {(wa.flows || []).length > 0 && (
            <details className="mb-3 bg-stone-50 rounded-xl border border-stone-200">
              <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold text-stone-700">🔀 זרימות ({wa.flows.length})</summary>
              <div className="px-4 pb-3 divide-y divide-stone-100">
                {wa.flows.map((f, i) => (
                  <div key={f.id || i} className="py-2">
                    <p className="font-bold text-sm">{f.name || '—'}</p>
                    <p className="text-[10px] text-stone-400" dir="ltr">id: {f.id}</p>
                  </div>
                ))}
              </div>
            </details>
          )}
          {(wa.messages || []).length > 0 && (
            <details className="mb-3 bg-stone-50 rounded-xl border border-stone-200">
              <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold text-stone-700">💬 הודעות ({wa.messages.length})</summary>
              <div className="px-4 pb-3 divide-y divide-stone-100 max-h-96 overflow-y-auto">
                {wa.messages.map((m, i) => (
                  <div key={m.id || i} className="py-2">
                    <div className="flex justify-between text-[10px] text-stone-400 mb-0.5">
                      <span dir="ltr">{m.from || m.phone || m.to || '—'}</span>
                      <span>{fmtDate(m.timestamp || m.created_at)}</span>
                    </div>
                    <p className="text-[12px] whitespace-pre-wrap">{m.body || m.text || m.message || '—'}</p>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Section>

        {/* System / Manifest */}
        <Section id="system" title="מערכת · Manifest · Integrations" color="stone" defaultOpen={false}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-black mb-2">Manifest</h4>
              <div className="space-y-1">
                <KV label="company_id" value={manifest.company_id} mono />
                <KV label="company_name" value={manifest.company_name} />
                <KV label="created_at" value={fmtDate(manifest.created_at)} />
                <KV label="schema_version" value={String(manifest.schema_version ?? '—')} />
                <KV label="kind" value={manifest.kind} />
                <KV label="files" value={String((manifest.files || []).length)} />
              </div>
            </div>
            <div>
              <h4 className="text-sm font-black mb-2">Integrations</h4>
              <div className="space-y-1">
                {Object.entries(integrations).map(([k, v]) => (
                  <KV key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} mono />
                ))}
                {Object.keys(integrations).length === 0 && <p className="text-stone-400 text-xs">אין נתונים</p>}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-black mb-2">Health Check</h4>
              <div className="space-y-1">
                {Object.entries(health).map(([k, v]) => (
                  <KV key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} mono />
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-black mb-2">Tenant</h4>
              <div className="space-y-1">
                <KV label="company_id" value={tenant.company_id} mono />
                <KV label="version" value={String(tenant.version ?? '—')} />
                <KV label="created_at" value={fmtDate(tenant.created_at)} />
              </div>
            </div>
          </div>
          {Object.keys(featureNotes).length > 0 && (
            <div className="mt-4 pt-3 border-t border-stone-200">
              <h4 className="text-sm font-black mb-2">Feature Notes</h4>
              <pre className="bg-stone-900 text-stone-100 text-[11px] p-3 rounded-lg overflow-x-auto" dir="ltr">{JSON.stringify(featureNotes, null, 2)}</pre>
            </div>
          )}
        </Section>

        <footer className="text-center text-[11px] text-stone-400 py-6" dir="ltr">
          Backup imported {fmtDate(manifest.created_at)} · {manifest.company_id}
        </footer>
      </main>
    </div>
  );
};

const Card = ({ label, value, color = 'stone', small }) => {
  const map = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    pink: 'bg-pink-50 border-pink-200 text-pink-700',
    stone: 'bg-stone-50 border-stone-200 text-stone-700',
  };
  return (
    <div className={`border rounded-xl p-3 ${map[color] || map.stone}`}>
      <p className={`font-bold ${small ? 'text-[10px]' : 'text-[11px]'} opacity-80`}>{label}</p>
      <p className={`font-black ${small ? 'text-base' : 'text-lg'} mt-0.5`}>{value}</p>
    </div>
  );
};

export default FullFrameBackup;
